import React, { FC } from 'react'
import { useParams } from 'react-router-dom'
import { Box, makeStyles } from 'transfix-ui/core'
import { ITrailerStatesEnum } from 'types/graphqlTypes'
import {
  TrailerNumber,
  StopType,
  NextAppointment,
  Lane,
  ShipmentDisplay,
  TrailerLocation,
} from 'apps/marketplace/components/TrailerRowDetails'
import { EditTrailer } from 'apps/marketplace/pages/shipment-details/components/ShipmentProgress/components/StopBasicInfo/AddTrailer/EditTrailer'
import { IShipmentResultFragment } from 'apps/marketplace/pages/shipment-details/components/ShipmentProgress/components/StopBasicInfo/AddTrailer/ShipmentAutosuggest/graphql/ShipmentResultFragment'
import { AddTrailer } from 'apps/marketplace/pages/shippers/components/ShipperTrailers/AddTrailerModal/AddTrailerModal'
import { EllipsisIcon } from 'transfix-icons'
import { useModal } from 'utils/hooks'
import CarrierTrailerRowContainer, {
  TrailerNumberContainer,
  StopTypeContainer,
  NextAppointmentContainer,
  LaneContainer,
  ShipmentContainer,
  LocationContainer,
  ActionsContainer,
} from './CarrierTrailerRowContainer'
import { useTrailerManagementContext } from './TrailerManagementContext'
import { ICarrierTrailerFragment } from './graphql/CarrierTrailerFragment'
import { useCarrierTrailers } from './hooks/useCarrierTrailers'

interface ICarrierTrailerProps {
  trailer: ICarrierTrailerFragment
  showMissingTrailers?: boolean
}

const useCarrierTrailerRowStyles = makeStyles(theme => ({
  root: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
  multipleItems: {
    display: 'flex',
    gap: 5,
    alignItems: 'center',
  },
  missing: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    height: '100%',
    gap: 3,
  },
  edit: {
    cursor: 'pointer',
  },
}))

const CarrierTrailerRow: FC<ICarrierTrailerProps> = ({ showMissingTrailers = false, trailer }) => {
  const styles = useCarrierTrailerRowStyles()

  const { id: carrierId = '' } = useParams<{ id: string; tab: string }>()
  const { closeModal, isOpen, openModal } = useModal()
  const { carrier, note, number, shipment, shipperFacility, state, updatedAt } = trailer
  const {
    currentStopIndex: currentStopIndexProp,
    firstPickup,
    id: shipmentId,
    lastDelivery,
    state: shipmentState,
    stops,
    updatedAt: shipmentUpdatedAt = '',
  } = shipment || {}

  const { facility } = shipperFacility || {}
  const { name: facilityName } = facility || {}

  const currentStopIndex = currentStopIndexProp || 0
  const currentStop = stops?.[currentStopIndex]
  const nextStop = stops?.[currentStopIndex + 1]
  const currentStopType = currentStop?.stopType === 'pickup' ? 'P' : 'D'
  const nextStopType = currentStop?.stopType === 'pickup' ? 'P' : 'D'
  const currentStopName = `${currentStopType}${currentStop?.stopSequence}`
  const nextStopName = `${nextStopType}${nextStop?.stopSequence}`

  const { currentFilters } = useTrailerManagementContext()

  const { refresh } = useCarrierTrailers(currentFilters, carrierId)

  const successHandler = () => {
    refresh()
  }

  const isEditModalOpen = isOpen('edit-trailer-modal')
  const isAddModalOpen = isOpen('add-trailer-modal')

  const openModalHandler = () => {
    if (showMissingTrailers) openModal('add-trailer-modal')()
    else openModal('edit-trailer-modal')()
  }

  return (
    <CarrierTrailerRowContainer className={styles.root}>
      <TrailerNumberContainer>
        <TrailerNumber trailerNumber={number} />
      </TrailerNumberContainer>
      <StopTypeContainer>
        <StopType currentStop={currentStop} currentStopName={currentStopName} />
      </StopTypeContainer>
      <NextAppointmentContainer>
        <NextAppointment
          currentStop={currentStop}
          nextStop={nextStop}
          currentStopName={currentStopName}
        />
      </NextAppointmentContainer>
      <LaneContainer className={styles.multipleItems}>
        <Lane firstPickup={firstPickup} lastDelivery={lastDelivery} />
      </LaneContainer>
      <ShipmentContainer className={styles.multipleItems}>
        <ShipmentDisplay shipment={shipment} />
      </ShipmentContainer>
      <LocationContainer className={styles.multipleItems}>
        <TrailerLocation
          facilityName={facilityName || ''}
          note={note || ''}
          shipmentId={shipment?.id || ''}
          shipmentState={shipmentState}
          shipmentUpdatedAt={shipmentUpdatedAt}
          showMissingTrailers={showMissingTrailers}
          stopName={nextStop ? nextStopName : currentStopName}
          trailerState={state}
          trailerUpdatedAt={updatedAt}
        />
      </LocationContainer>
      <ActionsContainer className={styles.edit}>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          height='100%'
          onClick={openModalHandler}>
          <EllipsisIcon />
        </Box>
      </ActionsContainer>
      {isEditModalOpen && (
        <EditTrailer
          trailerId={trailer.id}
          onClose={closeModal}
          open
          carrierApplicationId={carrierId}
          onSuccess={successHandler}
        />
      )}
      {isAddModalOpen && (
        <AddTrailer
          onClose={closeModal}
          open
          onSuccess={successHandler}
          defaultValues={{
            carrier: carrierId,
            shipment: shipmentId || '',
            status: ITrailerStatesEnum.assigned_to_shipment,
          }}
          carrierApplicationId={carrierId}
          AddTrailerFormProps={{
            shipment: shipment as IShipmentResultFragment,
            carrierApplication: carrier || undefined,
            shipmentDisabled: true,
            displayStatusOptionsGenerator: statuses =>
              statuses.filter(({ id }) => id === 'assigned_to_shipment'),
          }}
        />
      )}
    </CarrierTrailerRowContainer>
  )
}

export default CarrierTrailerRow
